import _ from 'lodash';
import {DateTime} from 'luxon';
import {createSelector} from 'reselect';

import {
  getRecurringEventInstanceStartTimes,
  isUnsavedDraftCalendarEventVersion,
} from '../../../../srcCommon/helpers/models/calendarEventVersionHelpers';
import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {CalendarEventVisualStatusesEnum} from '../../../models/api/calendarEventModel';
import {getHasNewRecurrenceCilent} from '../../featureFlags/featureFlagsSelectors';
import {CalendarEventParameters} from '../../panels/calendars/calendarsPanelState';
import {CalendarsParameters} from '../../panels/calendars/calendarsParameters';
import {
  findParametersCalendarVersionIds,
  findParametersRange,
} from '../../panels/calendars/calendarsParametersHelpers';
import {getCalendarEvents, getCalendarEventVersions, getCalendarVersions} from '../dataSelectors';

export function makeFindGetCalendarEventParametersList(
  getCalendarsParameters: AppParametricSelector<any, CalendarsParameters>,
  getCalendarsEventVersionUids: AppParametricSelector<any, ReadonlyArray<string>>,
) {
  return createSelector(
    [
      getCalendarsParameters,
      getCalendarsEventVersionUids,
      getCalendarVersions,
      getCalendarEvents,
      getCalendarEventVersions,
      getHasNewRecurrenceCilent,
    ],
    (
        calendarsParameters,
        calendarsEventVersionUids,
        calendarVersions,
        calendarEvents,
        calendarEventVersions,
        hasNewRecurrenceCilent,
      ) =>
      () => {
        const calendarEventVersionsInView = _(calendarsEventVersionUids)
          .map((uid) => calendarEventVersions[uid])
          .compact()
          .value();

        const masterEventToExceptionEventsMap = _(calendarEventVersionsInView)
          .map((v) => calendarEvents[v.calendarEventUid])
          .compact()
          .filter((e) => Boolean(e.recurringCalendarEventId))
          .groupBy((e) => e.recurringCalendarEventId)
          .value();

        return _(calendarEventVersionsInView)
          .map((calendarEventVersion) => {
            const calendarEvent = calendarEvents[calendarEventVersion.calendarEventUid];
            if (!calendarEvent || isUnsavedDraftCalendarEventVersion(calendarEventVersion)) {
              return null;
            }

            // Dont display the event on the calendar grid if its removed.
            if (calendarEventVersion.isRemoved) {
              return null;
            }

            const calendarVersionIds = findParametersCalendarVersionIds(calendarsParameters);
            return _(calendarEventVersion.calendarVersionIds)
              .filter((calendarVersionId) => calendarVersionIds.includes(calendarVersionId))
              .map((calendarVersionId) => {
                const calendarVersion = calendarVersions[calendarVersionId];
                if (!calendarVersion) {
                  return null;
                }

                // If this not a recurring event, simply add it to the list.
                if (!calendarEvent.recurrence || calendarEvent.recurrence.length === 0) {
                  const eventParameters: CalendarEventParameters = {
                    calendarEventVersionUid: calendarEventVersion.uid,
                    calendarVersionId,
                    instanceStartDate: undefined,
                  };

                  return [eventParameters];
                }

                if (calendarEvent.visualStatus !== CalendarEventVisualStatusesEnum.COMPLETE) {
                  return null;
                }

                // If this is a recurring event, we have to generate instance events from the series master event.
                // To do so, we first have to retrieve all the exception events within this series.
                const exceptionEvents = (masterEventToExceptionEventsMap[calendarEvent.id] || []).filter(
                  (exceptionEvent) => {
                    // Include the exception event if it has versions that are on the calendarVersion we're building events for.
                    const exceptionVersions = _(exceptionEvent.calendarEventVersionUids)
                      .map((uid) => calendarEventVersions[uid])
                      .compact()
                      .filter((v) => v.calendarVersionIds.includes(calendarVersion.id))
                      .value();

                    return exceptionEvent.originalStartDate && exceptionVersions.length > 0;
                  },
                );

                const {startsAt, endsAt} = findParametersRange(calendarsParameters);
                const instanceStartTimes = getRecurringEventInstanceStartTimes(
                  calendarEventVersion,
                  calendarEvent,
                  exceptionEvents,
                  // Capture all day recurring events at the start of the range by expanding by 1 day.
                  DateTime.fromMillis(startsAt).minus({day: 1}).toMillis(),
                  DateTime.fromMillis(endsAt).toMillis(),
                  hasNewRecurrenceCilent,
                );

                return instanceStartTimes.map((date) => {
                  const eventParameters: CalendarEventParameters = {
                    calendarEventVersionUid: calendarEventVersion.uid,
                    calendarVersionId: calendarVersion.id,
                    instanceStartDate: date.valueOf(),
                  };

                  return eventParameters;
                });
              })
              .flatten()
              .compact()
              .value();
          })
          .flatten()
          .compact()
          .value();
      },
  );
}
